import { PageHeading } from '@/components/Layout.tsx'
import { Refresh } from '@/components/Icons.tsx'
import { Seedling } from '@/components/Botanical.tsx'
import { useNews } from '@/features/news/useNews.ts'
import { timeAgo } from '@/lib/format.ts'
import type { Story } from '@/lib/types.ts'

function StoryItem({ story, index }: { story: Story; index: number }) {
  return (
    <li className="animate-unfurl" style={{ animationDelay: `${index * 70}ms` }}>
      <a
        href={story.link}
        target="_blank"
        rel="noopener noreferrer"
        className="group grid grid-cols-1 gap-5 py-8 sm:grid-cols-[1fr_auto]"
      >
        <div className="max-w-measure">
          <p className="eyebrow">
            {story.source}
            {story.publishedAt && <span className="text-linen"> · </span>}
            {story.publishedAt && timeAgo(story.publishedAt)}
          </p>

          <h2 className="mt-2.5 font-display text-[1.55rem] leading-[1.24] transition-colors duration-300 group-hover:text-fern sm:text-[1.75rem]">
            {story.title}
          </h2>

          {story.summary && <p className="prose-soft mt-3 text-[0.95rem]">{story.summary}</p>}

          <span className="mt-4 inline-flex items-center gap-1.5 text-[0.76rem] uppercase tracking-[0.18em] text-sage">
            Read it
            <span className="inline-block transition-transform duration-300 ease-botanical group-hover:translate-x-1">→</span>
          </span>
        </div>

        {story.image && (
          <div className="order-first h-40 w-full overflow-hidden rounded-leaf border border-linen sm:order-none sm:h-32 sm:w-44">
            <img
              src={story.image}
              alt=""
              loading="lazy"
              className="h-full w-full object-cover opacity-90 transition-all duration-700 ease-botanical group-hover:scale-[1.04] group-hover:opacity-100"
              onError={(e) => { (e.currentTarget.parentElement as HTMLElement).style.display = 'none' }}
            />
          </div>
        )}
      </a>
    </li>
  )
}

export default function Reading() {
  const { stories, loading, error, reload } = useNews()

  return (
    <>
      <PageHeading eyebrow="Gathered this morning" title="Good news">
        Only the encouraging half of the internet. Refresh for a different handful — the sources
        are curated, and anything grim is filtered out before it reaches this page.
      </PageHeading>

      <button onClick={() => void reload()} disabled={loading} className="btn-ghost">
        <Refresh className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        {loading ? 'Gathering…' : 'Show me different ones'}
      </button>

      {error && (
        <div className="mt-10 flex flex-col items-center gap-4 py-12 text-center">
          <Seedling tone="var(--c-clay)" className="h-16 w-auto opacity-50" />
          <p className="prose-soft max-w-[24rem]">{error}</p>
        </div>
      )}

      {!error && (
        <ul className="mt-6 divide-y divide-linen">
          {stories.map((s, i) => (
            <StoryItem key={s.link} story={s} index={i} />
          ))}
        </ul>
      )}

      {!loading && !error && stories.length === 0 && (
        <p className="prose-soft mt-10">Nothing came back this time. Try again in a moment.</p>
      )}
    </>
  )
}
